// get and display user watchlist
import React, { Component, useEffect, useState, useReducer, Fragment } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Drawer, Grid, List, ListItem, ListItemButton, Tooltip, Stack, Snackbar } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { CSVLink } from "react-csv";
import { FacebookShareButton, FacebookIcon, TwitterIcon } from "react-share";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import IconButton from '@mui/material/IconButton';
import { DataGrid } from '@mui/x-data-grid';
import Select, { createFilter } from 'react-select';
import { FixedSizeList as RWList } from 'react-window';
import _debounce from 'lodash.debounce';
import { matchSorter } from 'match-sorter';

// local utils
// import { AddItemCanvas } from '../utils/ModalUtil';
import { DeleteUserList, UpdateUserList } from '../pages/SaveLoadList';
import { ServerErrorMessage } from '../pages/PageNotFound';
import { loadWatchlist } from '../actions/auth';
import { useWindowDimensions } from '../pages/Utils';

// local json files
import coins from "../data/coins.json";
import stocks from "../data/stocks.json";
import etfs from "../data/etfs_market_cap.json";


const stockList = stocks.map(v => ({ value: v.Ticker, label: v.Ticker + ' - ' + v.Name }));
const coinList = coins.map(v => ({ value: v.Ticker, label: v.Ticker + ' - ' + v.Name }));
const etfList = etfs.map(v => ({ value: v.Ticker, label: v.Ticker + ' - ' + v.Name }))
const options = stockList.concat(coinList).concat(etfList);

const windowHeight = 35;

class MenuList extends Component {
    render() {
        const { options, children, maxHeight, getValue } = this.props;
        const [value] = getValue();
        const initialOffset = options.indexOf(value) * windowHeight;

        return (
            <RWList
                height={maxHeight}
                itemCount={children.length}
                itemSize={windowHeight}
                initialScrollOffset={initialOffset}
            >
                {({ index, style }) => <div style={style}>{children[index]}</div>}
            </RWList>
        );
    }
}


const Manage = ({ isAuthenticated, user, stateWatchlist, loadWatchlist, navbarSize }) => {

    // console.log('ManagePage: ', stateWatchlist);
    
    const [openList, setOpenList] = useState(stateWatchlist.length > 0 ? stateWatchlist[0] : {});
    const [openlistName, setOpenlistName] = useState(stateWatchlist.length > 0 ? stateWatchlist[0].listname : null);
    const [openlistItems, setOpenlistItems] = useState(stateWatchlist.length > 0 ? stateWatchlist[0].items : []);
    const [activeButton, setActiveButton] = useState(0);
    const [serverError, setServerError] = useState(!openList.items || openList.items.length === 0);
    const [loading, setLoading] = useState(false);
    const [listCount, setListCount] = useState(stateWatchlist.length);
    const cardColor = '#f5f5f5';
    
    // console.log(stateWatchlist.length);

    useEffect(() => {
        if (openList.items && openList.items.length > 0) {
            setServerError(false);
        }
        setListCount(stateWatchlist.length);
    }, [stateWatchlist]);
    
    const handleListItemClick = (name, index) => {
        setOpenList(stateWatchlist[index]);
        setOpenlistName(name);
        setOpenlistItems(stateWatchlist[index].items);
        setActiveButton(index);
    };

    const handleKeyPress = _debounce( (e) => {

        if (e.keyCode === 40) {
            if (activeButton+1 < listCount) {
                setActiveButton(prev => prev+1)
            } else {
                setActiveButton(0)
            }
        } else if (e.keyCode === 38) {
            if (activeButton > 0) {
                setActiveButton(prev => prev -1)
            } else {
                setActiveButton(listCount-1)
            }
        };

    }, 300);

    useEffect(() => {
        setOpenList(stateWatchlist[activeButton]);
        setOpenlistName(stateWatchlist[activeButton]?.listname);
        setOpenlistItems(stateWatchlist[activeButton]?.items);
    }, [activeButton]);

    // delete watchlist from server and update userList state
    const [delSuccess, setDelSuccess] = useState(false);
    const [openSnack, setOpenSnack] = useState(false);
    const [snackMessage, setSnackMessage] = useState(null);

    const handleSnackClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnack(false);
    };

    const handleListDel = () => {
        if (!window.confirm(`Delete ${openlistName}?`)) return;

        const idToRemove = openList.id;
        const storageKey = 'public-list';

        DeleteUserList(user.uid, idToRemove, success => {
            if (!success) {
                setSnackMessage('Delete failed');
                setOpenSnack(true);
                return;
            }

            // 1) read & parse
            let stored = [];
            try {
                const raw = localStorage.getItem(storageKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    // Support both {data: [...]} and plain array
                    if (Array.isArray(parsed)) {
                        stored = parsed;
                    } else if (parsed && Array.isArray(parsed.data)) {
                        stored = parsed.data;
                    }
                }
            } catch {
                stored = [];
            }

            // 2) filter out exactly the one we deleted
            const filtered = stored.filter(wl => String(wl.id) !== String(idToRemove));

            // 3) always overwrite, but warn if nothing was removed
            if (filtered.length !== stored.length) {
                localStorage.setItem(storageKey, JSON.stringify({ data: filtered }));
            } else {
                console.warn(
                    `No item removed from public-list. Check that wl.id (${idToRemove}) matches the IDs stored in localStorage.`
                );
            }

            // 4) update your component state / re-fetch
            loadWatchlist(user);
            setSnackMessage('Deleted successfully');
            setOpenSnack(true);
        });
    };    

    // add to watchlist
    const [newItems, setNewItems] = useState([]);
    const [addSuccess, setAddSuccess] = useState(false);
    const [ignored, forceUpdate] = useReducer(x => x+1, 0);

    const handleAddClick = () => {
        setOpenList(stateWatchlist[activeButton]);
        setOpenlistItems(stateWatchlist[activeButton].items);
        openCanvas();
    }

    const updateAfterAdd = () => {
        if (addSuccess) {
            loadWatchlist(user);
            setOpenList(stateWatchlist[activeButton]);
            setOpenlistItems(stateWatchlist[activeButton].items);
            setOpenSnack(true);
            setSnackMessage('Watchlist was updated successfully.');
            forceUpdate();
        }
    };

    useEffect( () => {
        updateAfterAdd();
        setAddSuccess(false);
    }, [addSuccess]);


    const handleUpdateList = () => {
        closeCanvas();
        if (newItems.length > 0) {
            // Use symbol and name as per new structure
            const newItemsRename = newItems.map(v => ({ ticker: v.symbol, name: v.name }));
            try {
                UpdateUserList(openList.id, openlistName, newItemsRename, openList.public, user.uid, setAddSuccess);
                setNewItems([]);
                updateAfterAdd();
            } catch {
                setAddSuccess(false);
                setOpenSnack(true);
                setSnackMessage('Failed to update watchlist. Please try again.');
            }
        };
    };


    
    const columns = [
        { field: 'symbol', headerName: 'Symbol', width: 100 },
        { field: 'init_price', headerName: 'Initial Price', width: 100 },
        { field: 'curr_price', headerName: 'Current Price', width: 100 },
        { field: 'change', headerName: 'Change $', width: 100 },
        { field: 'percent_change', headerName: '% Change', width: 100 },
        { field: 'init_date', headerName: 'Initial Date', width: 150 },
    ];
    
    const [rows, setRows] = useState([])
    useEffect(() => {
        if (stateWatchlist.length > 0) {
            var wl = stateWatchlist[activeButton] ? stateWatchlist[activeButton] : stateWatchlist[0];
            setRows(
                Array.isArray(wl.items)
                    ? wl.items.map((item, index) => ({
                        id: index,
                        symbol: item.symbol,
                        init_price: Number(item.init_price).toFixed(3),
                        curr_price: Number(item.current_price).toFixed(3),
                        change: item.price_change,
                        percent_change: item.percent_change,
                        init_date: item.created ? item.created.split('T')[0] : (wl.created ? wl.created.split('T')[0] : ''),
                    }))
                    : []
            );
        } else {
            setRows([]);
        }
    }, [stateWatchlist, openList]);

    const [showCanvas, setShowCanvas] = useState(false);
    const closeCanvas = () => setShowCanvas(false);
    const openCanvas = () => setShowCanvas(true);


    // setting table dimensions *** potential to improve ***
    const mainBoxHeight = useWindowDimensions().height - navbarSize - 15;
    const tableHeight = useWindowDimensions().height - (2*navbarSize) - 15;


    return (
    <div> { loading ?
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <CircularProgress />
        </div> : serverError ?
        <div>
            <ServerErrorMessage />
        </div> :
        <div>
        <Grid container sx={{marginTop: '10px', height: mainBoxHeight}}>
            <Grid item xs={3} sx={{background: cardColor, height: mainBoxHeight, overflow: 'auto'}}>
                <LeftBar
                    userList={stateWatchlist}
                    activeButton={activeButton}
                    cardColor={cardColor}
                    handleListItemClick={handleListItemClick}
                    panelHeight={mainBoxHeight}
                    handleKeyPress={handleKeyPress}
                ></LeftBar>
            </Grid>

            <Grid item xs={9} sx={{paddingLeft: '10px'}}>
                <ActionBar
                    openList={openList}
                    openlistName={openlistName}
                    openlistItems={openlistItems}
                    handleAddClick={handleAddClick}
                    handleDelClick={handleListDel}
                ></ActionBar>
                <div style={{
                    backgroundColor: cardColor,
                    height: tableHeight,
                    minHeight: tableHeight,
                }}>
                    <DataGrid
                    rows={rows}
                    columns={columns}
                    />
                </div>

            </Grid>
        </Grid>
        <AddItemCanvas
            showCanvas={showCanvas}
            closeCanvas={closeCanvas}
            openCanvas={openCanvas}
            listName={openlistName}
            listItems={openlistItems}
            newItems={newItems}
            setNewItems={setNewItems}
            handleUpdateList={handleUpdateList}
        ></AddItemCanvas>
        <Snackbar
            open={openSnack}
            autoHideDuration={4000}
            onClose={handleSnackClose}
            message={snackMessage}
        ></Snackbar>
        </div>
        }
    </div>
    )
};

const mapStateToProps = (state) => {
    // Use new backend structure: flat array of watchlists
    let stateWatchlist = [];
    const wl = state.auth.watchlistData;
    if (Array.isArray(wl)) {
        stateWatchlist = wl;
    }
    return {
        isAuthenticated: state.auth.isAuthenticated,
        user: state.auth.user,
        stateWatchlist,
        navbarSize: state.barSize.navbarSize,
    };
};

export default connect( mapStateToProps, { loadWatchlist } ) (Manage);


function handleTweetMessage(al) {
    if (!al || !Array.isArray(al.items)) {
        return '';
    }
    const listItems = '$' + al.items.map(t => t.symbol).join(', $');
    const date = al.created ? al.created.split('T')[0] : '';
    const totalReturn = al.total_return || 0;
    return `Created: ${date}   Total Return: $${totalReturn}   ${listItems}`;
}


const LeftBar = ({
    userList,
    activeButton,
    cardColor,
    handleListItemClick,
    panelHeight,
    handleKeyPress,

}) => {

    return (
        <div style={{height: panelHeight, outline: 'none'}} onKeyDown={handleKeyPress} tabIndex='-1'>
            <List dense={true}>
                {userList.map((v, idx) => {
                    const totalReturn = v.total_return || 0;
                    return (
                        <Fragment key={idx}>
                            <ListItemButton
                                onClick={(e) => handleListItemClick(v.listname, idx)}
                                sx={{backgroundColor: idx === activeButton ? '#bbd9fb' : cardColor}}
                            >
                                <div style={{paddingLeft: '10px'}}>
                                    <h6>Name: { v.listname ? v.listname.toUpperCase() : <span style={{color: 'red'}}>No Name</span> }</h6>
                                    <h6>Date: { v.created ? v.created.split('T')[0] : 'N/A' }</h6>
                                    <h6>Return: ${totalReturn.toFixed(2)}</h6>
                                </div>
                            </ListItemButton>
                            <hr />
                        </Fragment>
                    );
                })}
            </List>
        </div>
    )

}


const ActionBar = ({
    openList,
    openlistName,
    openlistItems,
    handleAddClick,
    handleDelClick,
}) => {

    const tweetMessage = handleTweetMessage(openList);


    return (
        <Stack direction="row" sx={{background: 'honeydew'}}>
            <Grid item xs={6}>
                <Tooltip title="Add to Watchlist">
                <IconButton
                onClick = { (e) => handleAddClick(openlistName, openlistItems) }
                value={(openlistName, openlistItems)}
                >
                <AddCircleIcon fontSize='large' color='success' />
                </IconButton>
                </Tooltip>

                <Tooltip title="Delete the Watchlist">
                <IconButton onClick = { (e) => handleDelClick(openlistName) } value={openlistName}>
                <DeleteIcon fontSize='large' color='warning' />
                </IconButton>
                </Tooltip>
                <CSVLink
                    data={(Array.isArray(openlistItems) ? openlistItems : []).map((t) => ({
                        Symbol: t.symbol,
                        Name: t.name,
                        Price: t.init_price,
                        LastClose: t.last_close,
                        Change: t.price_change,
                        PercentChange: t.percent_change,
                        TotalReturn: t.total_return,
                    }))}
                    filename={openlistName + '.csv'}
                >
                <Tooltip title="Download the Watchlist">
                <IconButton>
                <DownloadForOfflineIcon fontSize='large' color='action' />
                </IconButton>
                </Tooltip>
                </CSVLink>
            </Grid>
            <Grid item xs={6} style={{display: 'flex', justifyContent: 'end'}}>
                <Tooltip title="Share on Twitter">
                <IconButton
                className="tweet"
                href={`https://twitter.com/intent/tweet?text=${tweetMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                >
                <TwitterIcon size={30} round={true} />
                </IconButton>
                </Tooltip>
                <FacebookShareButton url='https://watchlist101.com' quote={tweetMessage}>
                <Tooltip title="Share on Facebook">
                <IconButton>
                    <FacebookIcon size={30} round={true} />
                </IconButton>
                </Tooltip>
                </FacebookShareButton>
            </Grid>
        </Stack>
    )
};


// add item to watchlist from modal
const AddItemCanvas = ({
    showCanvas,
    closeCanvas,
    listName,
    listItems,
    newItems,
    setNewItems,
    handleUpdateList,
}) => {

    const handleSearchSelect = (e) => {
        if (e) {
            const tkr = e.value;
            const name = e.label;
            handleAdd(tkr, name);    
        }
    }


    // add new item(s) to watchlist
    const currentSymbols = 
        (Array.isArray(listItems) ? listItems : []).map(v => v.symbol)
        .concat((Array.isArray(newItems) ? newItems : []).map(v => v.symbol))
    const handleAdd = (tkr, name) => {
        if (currentSymbols.includes(tkr)) {
            alert('This ticker is already in the list.');
        } else {
            setNewItems([...newItems, {symbol: tkr, name: name}])
        }
    };

    const [listOptions, setListOptions] = useState(options)

    return (
        <div>
            <Drawer
                anchor='right'
                open={showCanvas}
                onClose={closeCanvas}
                PaperProps={{
                    sx: {width: '30%'}
                }}
            >
            <Card style={{display: 'flex', justifyContent: 'center', padding: '5px'}}>
                <Button variant="contained" disabled>ADD TO: {listName}</Button>
            </Card>
            <Select
                components={{ MenuList }}
                defaultValue={ null }
                onChange={handleSearchSelect}
                options={listOptions}
                isClearable={true}
                placeholder={'Search by name, ticker, ...'}
                filterOption={createFilter({
                    ignoreAccents: false,
                })}
                getOptionLabel={(option) => `${option.value} : ${option.label}`}
                onInputChange={ inputValue => {
                    setListOptions(matchSorter(options, inputValue, {keys: ['value', 'label']}).splice(0,25))
                }}
            />
            <br/>
            <Card style={{
                maxHeight: '60vh',
                overflow: 'auto',
                backgroundColor: '#f5f5f5',
            }}>
                <List>
                    { (Array.isArray(listItems) ? listItems : [])
                        .concat(Array.isArray(newItems) ? newItems : [])
                        .map((item, index) => (
                            <ListItem key={index}>{ item.symbol }</ListItem>
                        ))
                    }</List>
            </Card>
            <br/>
            <Card style={{display: 'flex', justifyContent: 'center'}}>
                <Button variant="secondary" onClick={handleUpdateList} className="w-100">UPDATE WATCHLIST</Button>
            </Card>
            </Drawer>
        </div>
    );
};

